import api from '@common/api';
import { InvestorAccountModel } from '@common/models/InvestorAccountModel';
import { SelfAccreditationClaims } from '@common/models/InvestorAccountModel/types';
import { PropertiesModel } from '@common/models/PropertiesModel';
import { WaitlistModel } from '@common/models/WaitlistModel';
import session from '@common/session';
import { SubscriptionOfferHint } from '@services/appsync/types';
import { getCurrentUser } from 'aws-amplify/auth';
import create from 'zustand';

interface PropertyDetailViewModelProps {
  ctaState: 'INVEST' | 'CAPITAL_FULL' | 'NON_ACCREDITED_SLOTS_FULL' | 'CHECKING' | 'UNAUTHENTICATED' | 'WAITLIST';
  pageState: 'LOADING' | 'ERROR' | 'PROPERTY_DETAIL';
  position?: number | null;
}
interface PropertyDetailViewModelActions {
  init: (slug: string) => Promise<boolean>;
  joinWaitlist: () => Promise<{ error?: string; success: boolean }>;
  setCTAState: (pageState: PropertyDetailViewModelProps['ctaState']) => void;
  setPageState: (pageState: PropertyDetailViewModelProps['pageState']) => void;
  updateAccreditationClaims: (claims: SelfAccreditationClaims) => Promise<{ error?: string; success: boolean }>;
}
export const usePropertiesDetailViewModel = create<PropertyDetailViewModelProps & PropertyDetailViewModelActions>(
  (set, get) => ({
    ctaState: 'CHECKING',
    init: async (slug) => {
      set({ pageState: 'LOADING' });

      // TODO: We should start using Amplify's Auth context provider to
      // standardize authentication state access.
      // https://ui.docs.amplify.aws/react/connected-components/authenticator
      let isAuthenticated = false;
      try {
        isAuthenticated = Boolean(await getCurrentUser());
      } catch (err) {
        isAuthenticated = false;
      }

      // get the investor account if it's an authenticated user
      let investorAccount = null;
      if (isAuthenticated) {
        investorAccount = InvestorAccountModel.getState()?.investorAccount ?? null;
        if (!investorAccount) {
          const result = await InvestorAccountModel.getState().fetchInvestorAccount();
          investorAccount = result.investorAccount ?? null;
        }
      }

      // make sure the property exists
      const propertiesModel = PropertiesModel.getState();
      const property = await propertiesModel.fetchProperty(slug, investorAccount?.id ?? null);
      if (!property) return false;
      if (property) set({ pageState: 'PROPERTY_DETAIL' });

      // determine offer access pageState
      // get the user's email to lookup their position on the waitlist
      const email = session.get()?.email;
      if (email) {
        // get waitlist
        const waitlistModel = WaitlistModel.getState();
        if (!waitlistModel.waitlistPosition) await waitlistModel.fetchWaitlistPosition(email);
      }

      // Determine the CTA state
      // if this hint is present, the user is not accredited and the waitlist needs to be shown
      if (property?.hints?.includes(SubscriptionOfferHint.CHECK_WAITLIST)) {
        set({ ctaState: 'WAITLIST' });
      }
      // if the capital is full, nobody can invest
      else if (property?.hints?.includes(SubscriptionOfferHint.CAPITAL_FULL)) {
        set({ ctaState: 'CAPITAL_FULL' });

        // if this hint is present, the user is not accredited and the slots are full
      } else if (property?.hints?.includes(SubscriptionOfferHint.NON_ACCREDITED_SLOTS_FULL)) {
        set({ ctaState: 'NON_ACCREDITED_SLOTS_FULL' });
        // else, let them invest
      } else if (!isAuthenticated) {
        set({ ctaState: 'UNAUTHENTICATED' });
      } else {
        set({ ctaState: 'INVEST' });
      }

      // return status of property to cleanly handle 404s
      return property ? true : false;
    },
    joinWaitlist: async () => {
      const email = session.get()?.email;
      if (!email) throw new Error('No Email');
      try {
        await api.waitlistAdd({ email });
        return { success: true };
      } catch {
        return { error: 'Unable to add user to waitlist', success: false };
      }
    },
    pageState: 'LOADING',
    setCTAState: (ctaState) => set({ ctaState }),
    setPageState: (pageState) => set({ pageState }),
    updateAccreditationClaims: async (claims) => {
      const result = await InvestorAccountModel.getState().updateAccreditationInformation(claims);
      return result;
    },
  }),
);
